import { ChangeEvent, HTMLProps, useId } from "react";
import * as RadixSelect from "@radix-ui/react-select";
import { Label } from "@radix-ui/react-label";
import { cn } from "@/utils/helpers";
import { SvgIconCaret } from "@/components/common/Svg";
import Button, { ButtonProps } from "@/components/common/Button";
import Overlay from "@/components/common/Overlay";
import fieldStyles from "@/components/common/Field/Field.module.css";
import styles from "./Select.module.css";

export interface SelectOptionProps extends HTMLOptionElement {
	small?: boolean;
}

export interface SelectProps extends HTMLProps<HTMLSelectElement> {
	label?: string;
	options?: SelectOptionProps[];
	disabled?: boolean;
	buttonProps?: ButtonProps;
	contentProps?: HTMLProps<HTMLDivElement>;
	onChange?: (value: any) => void;
}

export default function Select({
	name,
	label,
	value,
	defaultValue,
	options,
	disabled,
	buttonProps = {},
	contentProps = {},
	onChange,
	required,
	className
} : SelectProps) {
	const id = useId();

	const handleNativeChange = (event: ChangeEvent<HTMLSelectElement>) => {
		if(onChange) onChange(event?.target?.value);
	}

	return (
		<div
			className={cn(
				styles.Select,
				className
			)}
		>
			{label ?
				<Label
					htmlFor={id}
					className={styles.SelectLabel}
				>
					{label}
					{required ?
						<span
							className={fieldStyles.FieldRequiredAsterisk}
						>
							*
						</span>
					: null}
				</Label>
			: null}
			<RadixSelect.Root
				name={name}
				value={value ? String(value) : undefined}
				defaultValue={defaultValue ? String(defaultValue) : undefined}
				disabled={disabled}
				onValueChange={onChange}
			>
				<RadixSelect.Trigger
					id={id}
					asChild={true}
				>
					<Button
						filled={true}
						color="secondary"
						{...buttonProps}
						className={cn(
							styles.SelectTrigger,
							buttonProps.className
						)}
						after={
							<RadixSelect.Icon
								asChild
								className={styles.SelectIcon}
							>
								<SvgIconCaret
									type="down"
								/>
							</RadixSelect.Icon>
						}
					>
						<RadixSelect.Value
							placeholder="Select an option"
							// placeholder={getLang(name, defaultValue)}
						/>
					</Button>
				</RadixSelect.Trigger>
				<RadixSelect.Portal>
					<RadixSelect.Content
						asChild
						position="popper"
						side="bottom"
						sideOffset={4}
						avoidCollisions={true}
						{...contentProps}
					>
						<Overlay
							className={cn(
								styles.SelectContent,
								contentProps.className
							)}
						>
							<RadixSelect.Viewport
								className={styles.SelectViewport}
							>
								<RadixSelect.Group
									className={styles.SelectGroup}
								>
									{options?.map((option, index) =>
										<RadixSelect.Item
											key={index}
											value={option.value}
											disabled={option.disabled}
											className={cn(
												styles.SelectItem,
												option.small ? styles.SelectItem_small : null
											)}
										>
											<RadixSelect.ItemText>
												{option.label}
											</RadixSelect.ItemText>
										</RadixSelect.Item>
									)}
								</RadixSelect.Group>
							</RadixSelect.Viewport>
						</Overlay>
					</RadixSelect.Content>
				</RadixSelect.Portal>
			</RadixSelect.Root>
			<div
				className={styles.SelectNative}
			>
				<select
					name={name}
					value={value}
					aria-label={label}
					className={styles.SelectNativeSelect}
					onChange={handleNativeChange}
				>
					{options?.map((option, index) =>
						<option
							key={index}
							value={option.value}
							disabled={option.disabled}
						>
							{option.label}
						</option>
					)}
				</select>
				<SvgIconCaret
					type="down"
					className={styles.SelectNativeCaret}
				/>
			</div>
		</div>
	);
};